// src/components/LocationSelectionModal.jsx
import React, { useState, useEffect } from 'react';
import { getAvailableLocations } from '../utils/locationUtils';
import './LocationSelectionModal.css';

/**
 * Modal per la selezione della posizione di timbratura
 * Usato per timbrature da dispositivi non-kiosk (Admin, Responsabile, CapoOfficina)
 */
const LocationSelectionModal = ({ 
  isOpen, 
  onClose, 
  onConfirm,
  userName = 'Dipendente',
  scanType = 'in'
}) => {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadLocations();
    }
  }, [isOpen]);

  const loadLocations = async () => {
    setIsLoading(true);
    try {
      const availableLocations = await getAvailableLocations();
      setLocations(availableLocations);
    } catch (error) {
      console.error('Errore caricamento locations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = () => {
    if (!selectedLocation) {
      alert('⚠️ Seleziona una posizione prima di confermare');
      return;
    }
    
    onConfirm(selectedLocation);
    handleClose();
  };

  const handleClose = () => {
    setSelectedLocation(null);
    setSearchTerm('');
    onClose();
  };

  // Filtra locations in base alla ricerca
  const filteredLocations = locations.filter(location =>
    location.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  // Solo per timbrature di ingresso mostriamo il modal
  if (scanType !== 'in') {
    // Per uscite, conferma immediatamente senza chiedere posizione
    onConfirm(null);
    return null;
  }

  return (
    <div className="location-modal-overlay" onClick={handleClose}>
      <div className="location-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="location-modal-header">
          <h2>📍 Seleziona Posizione Timbratura</h2>
          <button className="close-modal-btn" onClick={handleClose}>✕</button>
        </div>

        {/* Info dipendente */}
        <div className="location-modal-info">
          <p>
            <strong>Dipendente:</strong> {userName}
          </p>
          <p className="info-text">
            ℹ️ Seleziona la posizione di timbratura per l'<strong>INGRESSO</strong>
          </p>
        </div>

        {/* Barra ricerca */}
        <div className="location-search-container">
          <input
            type="text"
            className="location-search-input"
            placeholder="🔍 Cerca cantiere o officina..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
        </div>

        {/* Lista locations */}
        <div className="locations-list">
          {isLoading ? (
            <div className="loading-locations">
              <div className="spinner"></div>
              <p>Caricamento posizioni...</p>
            </div>
          ) : filteredLocations.length === 0 ? (
            <div className="no-locations">
              <p>📭 Nessuna posizione trovata</p>
            </div>
          ) : (
            filteredLocations.map(location => (
              <div
                key={location.id}
                className={`location-item ${selectedLocation === location.id ? 'selected' : ''}`}
                onClick={() => setSelectedLocation(location.id)}
              >
                <div className="location-icon">
                  {location.tipo === 'officina' ? '🔧' : '🏗️'}
                </div>
                <div className="location-details">
                  <div className="location-name">{location.nome}</div>
                  {location.indirizzo && (
                    <div className="location-address">{location.indirizzo}</div>
                  )}
                </div>
                <div className="location-selector">
                  {selectedLocation === location.id && (
                    <span className="check-icon">✓</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer con pulsanti */}
        <div className="location-modal-footer">
          <button className="btn-cancel" onClick={handleClose}>
            Annulla
          </button>
          <button 
            className="btn-confirm" 
            onClick={handleConfirm}
            disabled={!selectedLocation}
          >
            ✓ Conferma Timbratura
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationSelectionModal;